import { ClientJS } from 'clientjs'
import * as R from 'ramda'

/**
 * Formats a trade value to 3 decimals if the last decimal is non-zero and to 2 decimals otherwise
 */
export const formatTradeValue = (price: number): string => {
  const threeDecimal = price.toFixed(3)

  if (threeDecimal.charAt(threeDecimal.length - 1) === '0') {
    return threeDecimal.substring(0, threeDecimal.length - 1)
  }

  return threeDecimal
}

export const isInIframe = () => {
  if (process.client) {
    return window.self !== window.top
  }
}

/**
 * Lazily get some gl parameters to make decisions about device capabilities.
 */
export const getDeviceWebGLMaxTextureLimit = () => {
  return getCapabilities().MAX_TEXTURE_SIZE
}

// Lazily get device capabilities
const getCapabilities = (() => {
  let capabilities: { MAX_TEXTURE_SIZE: number } | null = null

  return () => {
    if (capabilities) {
      return capabilities
    }

    try {
      // Cache them in case of context loss
      const gl = document.createElement('canvas').getContext('webgl')
      capabilities = {
        MAX_TEXTURE_SIZE: gl?.getParameter(gl.MAX_TEXTURE_SIZE),
      }
    } catch (e) {
      // Getting the context failed for some reason
      // Just assign some low defaults and hope it's fine when we try to get the real context
      capabilities = {
        MAX_TEXTURE_SIZE: 2048, // Assumed to be low
      }
    }

    return capabilities
  }
})()

// isIpad() isn't a reliable way to check for being iPad due to an odd choice of defaults on newer iPads where they lie about
// their user agents https://developer.apple.com/forums/thread/119186. Fall back is checking the number of touch points and
// assuming it's a mobile device if it has any
export const isLowEndDevice = () => {
  if (process.client) {
    const clientJS = new ClientJS()

    return clientJS.isMobile() || clientJS.isIpad() || ((navigator.maxTouchPoints ?? 0 > 0) && !clientJS.isWindows())
  }
}

// https://stackoverflow.com/questions/3446170/escape-string-for-use-in-javascript-regex/6969486#6969486
export function makeEscapedRegExp(str: string) {
  return new RegExp(str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i')
}

/**
 * Run a callback function after an event hasn't been fired for a specified period of time (ms)
 * @param callback - callback method to be executed after ms delay
 * @param ms - delay in milliseconds
 * @param immediate - whether to run the callback immediately and then wait for the delay to be run again
 */
export const debounce = function (func: (...args: any[]) => void, wait: number, immediate = false) {
  let timeout: NodeJS.Timeout | null

  return function executedFunction(...args: any[]) {
    // @ts-ignore
    let context = this

    let later = function () {
      timeout = null
      // @ts-ignore
      func.apply(context, args)
    }

    let callNow = immediate && !timeout

    clearTimeout(timeout!)

    timeout = setTimeout(later, wait)

    // @ts-ignore
    if (callNow) func.apply(context, args)
  }
}

export const throttle = (func: (param?: any) => void, timeFrame: number) => {
  let lastTime = 0
  return function (...args: any[]) {
    const now = Date.now()
    if (now - lastTime >= timeFrame) {
      // @ts-ignore
      func.apply(this, args)
      lastTime = now
    }
  }
}

/**
 * Returns true if n1 is a descendant of n2 in the DOM tree.
 */
export function isChildOf(n1: Node, n2: HTMLElement | undefined) {
  if (!n1 || !n2) return false
  return 0 < (n1.compareDocumentPosition(n2) & Node.DOCUMENT_POSITION_CONTAINS)
}

export const isBrowserSupported = (client: ClientJS, agent: NavigatorID['userAgent']) => {
  const isBrowserEdge = 'Edge'
  const isBrowserChromium = 'Edg' // -- for chromium based edge (dev or canary) and older versions of Edge
  const isBrowserVersionSupported = _isBrowserVersionSupported(agent)
  const browserVersion = parseFloat(client.getBrowserVersion())
  const supportedBrowsers = [
    // Supported browsers for AV: https://docs.aws.amazon.com/chime/latest/dg/meetings-sdk.html#mtg-limits
    client.isOpera() && browserVersion >= 66,
    client.isSafari() && browserVersion >= 10,
    client.isChrome() && browserVersion >= 80,
    client.isFirefox() && browserVersion >= 70,
    agent.indexOf(isBrowserEdge) > -1 && isBrowserVersionSupported('Edge/', 18),
    agent.indexOf(isBrowserChromium) && isBrowserVersionSupported('Edg/', 80),
  ]

  return supportedBrowsers.some(b => b === true)
}

const _isBrowserVersionSupported = R.curry((agent: NavigatorID['userAgent'], currentBrowser: string, acceptedVersion: number) => {
  const currentVersion = parseFloat(agent.slice(agent.indexOf(currentBrowser)).split('/')[1])

  return isNaN(currentVersion) ? false : currentVersion >= acceptedVersion
})

export function outersect(listA: any[], listB: any[], getComparator = R.identity) {
  const result = []
  const set = new Set(listA.map(getComparator))

  for (const item of listB) {
    if (!set.has(getComparator(item))) {
      result.push(item)
    }
  }

  return result
}

export const getInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map(x => x.charAt(0).toUpperCase())
    .join('')

/**
   See if the string representation of this base 10 number is exactly equal to the number itself.
   @param n string containing a number
   @returns boolean indicating if the representation was identical
 */
const isNumeric = (n: string) => parseInt(n, 10).toString(10) === n

/**
   Parse a hyphen-delimited range, e.g. 6-10, 5-21
   Start must be ≤ the end of the range.
   @param stringRepresentation a string such as 6-10, 5-21
   @returns an array of numbers inside the range (inclusive of start and end), or undefined if not valid
 */
function parseRange(stringRepresentation: string) {
  const [from, to] = stringRepresentation.split('-')
  if (isNumeric(from) && isNumeric(to) && +to >= +from) {
    return Array.from(Array(+to - +from + 1)).map((x, i) => i + +from)
  }
}

/**
   Parse a user-provided, comma-delimited list of numbers and ranges
   @param userInput a string user input, e.g. 1, 2, 4-6, 10
   @returns A validated array of numbers, or null if the input wasn't well-formed.
 */
export function parseRanges(userInput: string): number[] | null {
  // strip whitespace
  const noWhitespace = userInput.replace(/\s/g, '')

  // split and remove blanks
  const ranges = noWhitespace.split(',').filter(x => x)

  // explode ranges
  const expandedRanges = ranges.reduceRight((acc: number[], cur: string) => {
    if (typeof cur !== 'string') {
      return acc
    }

    // single page - convert to positive number
    if (isNumeric(cur) && +cur > 0) {
      acc.unshift(+cur)
      return acc
    }

    // range of pages - add all of them in
    const explodedRange = parseRange(cur)
    if (explodedRange) {
      acc.unshift(...explodedRange)
    }

    return acc
  }, [])

  // Invalid if any items were not numeric or valid numeric ranges
  if (expandedRanges.some(x => typeof x !== 'number')) {
    return null
  }

  // Make unique
  return Array.from(new Set(expandedRanges))
}
